import React, {useEffect, useState} from 'react';

import {
  Box,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack, Tooltip, Typography,
  useTheme
} from '@mui/material';

import styles from './SelectedMediaGroupView.module.css';

import {Image, ImageState} from '../../api/caseApiTypes';
import {useAppDispatch, useAppSelector} from '../../hooks/useAppRedux';
import {ImageCard} from '../image/imageCard/ImageCard';
import {
  CreateRetouchCaseFromSelectionButton
} from '../images/selectedImagesContext/CreateRetouchCaseFromSelectionButton';
import {DownloadSelectedMediaButton} from '../images/selectedImagesContext/DownloadSelectedMediaButton';
import {ImageFullScreenDisplay} from '../images/ImageFullScreenDisplay';
import {ImagesGridDetailsView} from '../images/detailsView/ImagesGridDetailsView';
import {useClearSelectedMedia} from '../images/selectedImagesContext/useClearSelectedMedia';
import {getSelectedMediaGroup, updateImages} from './mediaGroupsSlice';
import {setSelectedMedia, resetSelectedContext, searchInMediaGroup, exportAsPdf} from '../images/imagesSlice';
import {useSelectionContext} from '../images/selectedImagesContext/selectionContextContext';
import {OutlinedLabel} from '../../helpers/OutlinedLabel';
import {
  CheckBox,
  CheckBoxOutlineBlank, DriveFileMove, FolderShared, IndeterminateCheckBox,
  Info,
  InfoOutlined,
  Photo,
  PictureAsPdf,
} from '@mui/icons-material';
import {
  RemoveSelectedFromClipbookIconButton
} from '../images/selectedImagesContext/RemoveSelectedFromClipbookIconButton';
import {MediaGroupDetailsView} from './components/MediaGroupDetailsView';
import {AddSelectedToClipbookIconButton} from '../images/selectedImagesContext/AddSelectedToClipbookIconButton';
import {useUserFilters} from '../../hooks/useUserFilters';
import {SearchContext, useImageSearchState} from '../../hooks/useSearchState';
import {SearchFilters, SearchFilterTranslator} from '../sidebar/filter/searchFilterTranslator';
import {FilterableImage} from '../../api/filterTypes';
import {ImagePropClauseResolverConfig} from '../sidebar/filter/propClauseResolver/resolverConfigs';
import {PublishGroupDialog} from './components/PublishGroupDialog';
import {LoadMoreItem} from '../../commonComponents/LoadMoreItem';
import {ImagesSearchPanel} from '../images/ImagesSearchPanel';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  useDraggable,
  useDroppable,
  closestCenter,
  MeasuringConfiguration,
  MeasuringStrategy
} from '@dnd-kit/core';

import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import {CSS} from '@dnd-kit/utilities';
import {DragIndicator} from '@mui/icons-material';

const translator = new SearchFilterTranslator<FilterableImage>(ImagePropClauseResolverConfig);

const pageSize = 50;

enum ImageProductionApprovalState {
  Approved,
  ApprovedWhenPublished,
  NotApproved,
}

function getIsApproved(image: Image): ImageProductionApprovalState {
  if (image.state === ImageState.Approved) {
    return ImageProductionApprovalState.Approved;
  } else if (image.state === ImageState.ProofedOk) {
    // If the image is in the state Proofed OK, and the publication date has been reached, the image is
    // considered Approved.
    if (image.publicationDate && new Date(image.publicationDate) < new Date()) {
      return ImageProductionApprovalState.Approved;
    } else {
      return ImageProductionApprovalState.ApprovedWhenPublished;
    }
  }
  return ImageProductionApprovalState.NotApproved;
}

const SortableImageCard = React.memo(
  ({
     image,
     style,
     activeIndex,
   }: {
    image: Image;
    style?: React.CSSProperties;
    activeIndex: number;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
      isSorting,
      index,
      over,
    } = useSortable({
      id: image.id,
      animateLayoutChanges: () => true,
    });

    const isDropTarget =
      over?.id === image.id &&
      index !== activeIndex;

    return (
      <Grid
        ref={setNodeRef}
        style={{
          ...style,
          transition,
          display: "flex",
          transform: isSorting
            ? undefined
            : CSS.Translate.toString(transform),
          opacity: isDragging ? 0.5 : 1,
          boxSizing: 'border-box',
          position: 'relative',
        }}
        {...attributes}
      >
        <ImageCard
          image={image}
          disableRemoval
        />

        <IconButton
          {...listeners}
          size="small"
          sx={{
            cursor: isDragging ? 'grabbing' : 'grab',
            flexShrink: 0,
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            padding: 0,
            alignSelf: 'center',
          }}
        >
          <DragIndicator />
        </IconButton>

        {isDropTarget && (
          <Box
            sx={{
              position: 'absolute',
              left: '-6px',
              top: '10px',
              bottom: '10px',
              width: '3px',
              backgroundColor: '#1976d2',
              borderRadius: '2px',
            }}
          />
        )}
      </Grid>
    );
  }
);


export const SelectedMediaGroupView: React.FC = () => {
  const dispatch = useAppDispatch();
  const [showInformation, setShowInformation] = useState<boolean>(false);
  const [continuation, setContinuation] = useState(0);
  const [query, setQuery] = useState('');

  const {user} = useAppSelector(state => state.app);

  const {palette} = useTheme();

  const isLightMode = palette.mode === 'light';

  let selectedMediaGroup = useAppSelector(getSelectedMediaGroup);

  const selectionContext = useSelectionContext();
  const selectedImages = useAppSelector(state => state.images.selected[selectionContext]);
  const anySelected = selectedImages.length > 0;
  const clearSelection = () => dispatch(resetSelectedContext(selectionContext));
  useClearSelectedMedia();

  const {items: images, count, isSearching} = useImageSearchState(SearchContext.MediaGroup);
  const [orderedImages, setOrderedImages] = useState<Image[]>([]);
  const userFilters = useUserFilters(SearchContext.MediaGroup);

  const [showPublishMediaGroupDialog, SetShowPublishMediaGroupDialog] = useState<boolean>(false);

  const pageSize = 50;
  const [offset, setOffset] = useState(0);

  const [canLoadMore, setCanLoadMore] = useState(false);

  const totalImages = selectedMediaGroup?.images.length ?? 0;

  const currentPageSize = Math.min(
    pageSize,
    totalImages - offset
  );

  const loadMore = () => {
    setContinuation(images.length);
  };

  const [activeId, setActiveId] = useState<string | null>(null);
  const activeImage = images.find(
    image => image.id === activeId
  );
  const activeIndex = activeId
    ? orderedImages.findIndex(image => image.id === activeId)
    : -1;

  useEffect(() => {
    if (userFilters && selectedMediaGroup) {
      if (selectedMediaGroup.images.length > 0) {
        dispatch(
          searchInMediaGroup({
            orderBy: [],
            query,
            filterTree: translator.getClauses({
              userFilters
            }),
            imageIds: selectedMediaGroup.images,
            continuation,
            pageSize,
            searchContext: SearchContext.MediaGroup,
          }),
        );
      }
    }
  }, [
    query,
    userFilters,
    continuation,
    selectedMediaGroup?.id,
  ]);

  useEffect(() => {
    if (images.length > 0) {
      setOrderedImages(images);
    }
  }, [images]);

  useEffect(() => {
    setOffset(0);
    setContinuation(0);
  }, [query, userFilters, selectedMediaGroup?.id]);

  useEffect(() => {
    const fetchedCount = images.length - continuation;

    setCanLoadMore(fetchedCount === pageSize);
  }, [images.length, continuation]);

  useEffect(() => {
    dispatch(resetSelectedContext(selectionContext));
  }, [query, dispatch, userFilters, selectionContext]);

  const getStyle = (image: Image) => {
    const style: React.CSSProperties = {
      borderStyle: 'solid',
      borderWidth: '1px',
    };

    const approvedState = getIsApproved(image);
    if (approvedState === ImageProductionApprovalState.Approved) {
      style.borderColor = isLightMode ? '#fff' : 'transparent';
    } else if (approvedState === ImageProductionApprovalState.ApprovedWhenPublished) {
      style.borderColor = isLightMode ? '#fff6bb' : '#888600';
      style.backgroundColor = isLightMode ? '#fffef1' : '#201d0c';
    } else {
      style.borderColor = isLightMode ? '#fbb' : '#800';
      style.backgroundColor = isLightMode ? '#fff1f1' : '#200c0c';
    }

    return style;
  };

  const getImageCard = (image: Image) => {
    return (
      <>
        <ImageCard key={image.id} style={getStyle(image)} image={image} disableRemoval/>
      </>
    );
  };

  const gridClasses = [styles.imageGrid];
  if (anySelected || showInformation) {
    gridClasses.push(styles.compact);
  }

  const additionalButtons = anySelected && (
    <OutlinedLabel label="Markerade bilder">
      <AddSelectedToClipbookIconButton/>
      <RemoveSelectedFromClipbookIconButton/>
      <CreateRetouchCaseFromSelectionButton/>
      <DownloadSelectedMediaButton/>
    </OutlinedLabel>
  );

  const toogleInformation = () => {
    setShowInformation(!showInformation);
  }

  const doExport = () => {
    if (window.confirm(`Vill du exportera aktiv grupp med dess filter till en Pdf?`)) {

      const imageGridFilters: SearchFilters<FilterableImage> = {
        defaultFilters: {
          Image_Ids: {value: selectedMediaGroup?.images || []},
        },
      };

      dispatch(
        exportAsPdf({
          filterTree: translator.getClauses({
            ...imageGridFilters,
            userFilters
          }),
          searchContext: SearchContext.MediaGroup,
        }),
      );
    }
  }

  const showExportToPdf = (): boolean => {

    return user?.email.toLowerCase() === 'lena.akilles@mio.se' ||
      user?.email.toLowerCase() === 'astrid.karlsson@mio.se' ||
      user?.email.toLowerCase() === 'eric.sundelin@mio.se' ||
      user?.email.toLowerCase() === 'julia.wadegard@mio.se' ||
      user?.email.toLowerCase() === 'xomptmag@mio.se' ||
      user?.email.toLowerCase() === 'xomptolf@mio.se' ||
      user?.email.toLowerCase() === 'xomptbjl@mio.se'
  }

  const showPublishMediaGroup = (): boolean => {

    return !selectedMediaGroup?.isPersonal && (
      user?.email.toLowerCase() === 'lena.akilles@mio.se' ||
      user?.email.toLowerCase() === 'astrid.karlsson@mio.se' ||
      user?.email.toLowerCase() === 'eric.sundelin@mio.se' ||
      user?.email.toLowerCase() === 'xomptmag@mio.se' ||
      user?.email.toLowerCase() === 'xomptolf@mio.se' ||
      user?.email.toLowerCase() === 'xomptbjl@mio.se')
  }

  const doPublish = () => {
    SetShowPublishMediaGroupDialog(true);
  }

  const additionalMediaGroupButtons = (
    <OutlinedLabel label="Grupp information">
      <div style={{minWidth: '6em'}}>
        <IconButton onClick={toogleInformation}>{showInformation ? <Info/> : <InfoOutlined/>}</IconButton>
        {showExportToPdf() && (
          <Tooltip title="Skapa Pdf">
            <IconButton onClick={doExport}>
              <PictureAsPdf/>
            </IconButton>
          </Tooltip>
        )}
        {showPublishMediaGroup() && (
          <Tooltip title={selectedMediaGroup?.published === null ? 'Publicera Grupp' : 'Redigera publicering'}>
            <IconButton title="" onClick={doPublish}>
              {selectedMediaGroup?.published === null ? (
                <DriveFileMove/>
              ) : (
                <FolderShared/>
              )}
            </IconButton>
          </Tooltip>
        )}
      </div>
    </OutlinedLabel>
  )

  const toogleAllSelectedButton = () => {
    if (selectedImages.length < images.length) {
      dispatch(setSelectedMedia({selectionContext, media: images}));
    } else {
      clearSelection();
    }
  };

  const measuring: MeasuringConfiguration = {
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  };

  return (
    <>
      {selectedMediaGroup && (
        <>
          <Box className={styles.container} style={{flexGrow: '1'}}>
            <Paper>
              <Stack sx={{p: '1em'}} direction="row">
                <Box className={styles.groupName}>{selectedMediaGroup?.name}</Box>
                {selectedMediaGroup?.images.length > 0 && (
                  <IconButton onClick={toogleAllSelectedButton}>
                    {
                      selectedImages.length === 0 ? (
                        <CheckBoxOutlineBlank/>
                      ) : selectedImages.length === images.length ? (
                        <CheckBox/>
                      ) : (
                        <IndeterminateCheckBox/>
                      )
                    }
                  </IconButton>
                )}

                <Box sx={{paddingRight: '10px'}}
                     className={styles.selectActionSelected}>
                  {selectedImages.length === images.length
                    ? "Avmarkera alla"
                    : "Markera alla"}
                </Box>

                <ImagesSearchPanel
                  className={styles.searchPanel}
                  onSearch={setQuery}
                  isSearching={isSearching}
                  count={count}
                />

                <Box sx={{flexGrow: 1}}/>
                <div>{additionalMediaGroupButtons}</div>
                <div>{additionalButtons}</div>
              </Stack>
            </Paper>

            <Box className={styles.content}>

              {isSearching && images.length === 0 && (
                <div className={styles.initLoading}>
                  <CardMedia title={'none...'} component={CircularProgress} disableShrink/>
                </div>
              )}

              {selectedMediaGroup?.images.length === 0 && (
                <Box className={styles.emptyGroup}>
                  <Photo sx={{height: '100px', width: '100px'}}/>
                  <Typography variant="subtitle1">Gruppen är tom</Typography>
                </Box>
              )}

              {selectedMediaGroup?.images.length > 0 && images.length > 0 && (

                <DndContext
                  collisionDetection={closestCenter}
                  measuring={measuring}

                  onDragStart={({active}) => {
                    setActiveId(String(active.id));
                  }}
                  onDragCancel={() => {
                    setActiveId(null);
                  }}

                  onDragEnd={ ({active, over}: DragEndEvent) => {
                    setActiveId(null);

                    if (!over || active.id === over.id) {
                      return;
                    }

                    const oldIndex = orderedImages.findIndex(
                      image => image.id === active.id
                    );

                    let newIndex = orderedImages.findIndex(
                      image => image.id === over.id
                    );

                    if (oldIndex === -1 || newIndex === -1) {
                      return;
                    }

                    if (oldIndex < newIndex) {
                      newIndex -= 1;
                    }

                    const newOrderedImages = arrayMove(
                      orderedImages,
                      oldIndex,
                      newIndex
                    );

                    const images = arrayMove(
                      selectedMediaGroup.images,
                      oldIndex,
                      newIndex
                    );

                     dispatch(
                       updateImages({
                         node: {
                           ...selectedMediaGroup,
                           images: images,
                         },
                         imageId: active.id.toString(),
                         newIndex,
                       })
                     );

                    setOrderedImages(newOrderedImages);
                  }}
                >
                  <SortableContext
                    items={orderedImages.map(image => image.id)}
                    strategy={rectSortingStrategy}>
                    <Grid
                      container
                      sx={{gap: '0.5em'}}
                      className={gridClasses.join(' ')}
                    >
                      {orderedImages.map(image => (
                        <SortableImageCard
                          key={image.id}
                          image={image}
                          style={getStyle(image)}
                          activeIndex={activeIndex}
                        />
                      ))}

                      {canLoadMore && (
                        <Grid item className={styles.loadMoreWrapper}>
                          <LoadMoreItem
                            className={styles.loadMoreContainer}
                            itemsLoaded={offset + currentPageSize}
                            count={totalImages}
                            pageSize={pageSize}
                            isSearching={isSearching}
                            loadMore={loadMore}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </SortableContext>

                  <DragOverlay>
                    {activeImage ? (
                      <ImageCard
                        style={{opacity: '0.3'}}
                        image={activeImage}
                        disableRemoval
                      />
                    ) : null}
                  </DragOverlay>

                </DndContext>
              )}

              {(anySelected || showInformation) && (
                <Box className={styles.details}>
                  {showInformation && (
                    <MediaGroupDetailsView
                      onClose={() => {
                        setShowInformation(false)
                      }}
                    />
                  )}

                  {anySelected && (
                    <>
                      <ImagesGridDetailsView onClose={clearSelection}/>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <ImageFullScreenDisplay/>
        </>
      )}

      <PublishGroupDialog
        isVisible={showPublishMediaGroupDialog}
        mediaGroup={selectedMediaGroup}
        images={images}
        closeDialog={() => {
          SetShowPublishMediaGroupDialog(false);
        }}
      />

    </>
  );
};
